import { memo, useCallback, useMemo } from 'react';
import {
  Form,
  Input,
  InputNumber,
  Select,
  Row,
  Col,
  Alert
} from 'antd';
import {
  IdcardOutlined,
  MailOutlined,
  PhoneOutlined,
  ShopOutlined
} from '@ant-design/icons';
import {
  ErpDataGrid,
  ErpDataGridStatusTag,
  ErpMasterShell,
  useErpMaster,
  formatINR
} from '../../components/common/erp-grid';
import {
  isEmail,
  isMobileIN,
  isGstin,
  digitsOnly,
  gstinInput
} from '../../utils/validators';
// Stage 6A-pre — Approval MFA wiring for the demo grid. Delete is
// protected by L1 Approval MFA; bulk delete is protected by L1 + L2
// Approval MFA. Real production modules are NOT touched; the demo
// hits the dedicated /api/approval-mfa/demo/* mock endpoints which
// only return success after the backend has validated the challenge.
import endpoints from '../../api/endpoints';

// Stage 6A foundation — sandbox / demo for the reusable ErpDataGrid.
// LOCAL STATE ONLY. No API calls, no DB writes, no impact on any
// real workflow. Safe to delete by removing this file + the route
// entry in routes/index.jsx and the sidebar link.

// ── Indian states with GST codes ─────────────────────────────
const STATES = [
  { code: '27', label: 'Maharashtra' },
  { code: '29', label: 'Karnataka' },
  { code: '33', label: 'Tamil Nadu' },
  { code: '36', label: 'Telangana' },
  { code: '24', label: 'Gujarat' },
  { code: '07', label: 'Delhi' },
  { code: '19', label: 'West Bengal' },
  { code: '32', label: 'Kerala' }
];

const HSN_CODES = ['7005', '7006', '7007', '7008', '7009', '7016'];

const STATUS_OPTIONS = [
  { label: 'Active',   value: 'active'   },
  { label: 'Inactive', value: 'inactive' },
  { label: 'Blocked',  value: 'blocked'  }
];

// ── Realistic glass-industry mock companies ──────────────────
// Preserved verbatim from the original spec so the demo reads like
// real customer master data, not lorem ipsum. Mixed in a couple of
// plausible variants to reach 25 rows.
const MOCK_COMPANIES = [
  'Sri Balaji Toughened Glass',
  'Chennai Safety Glass',
  'Kovai Glass Works',
  'Madurai Laminates',
  'Bengaluru Glass Solutions',
  'Foxconn Hon Hai Technology India',
  'Apex Facade Systems',
  'Greenline DGU Glass',
  'Crystal Edge Processors',
  'Tamilnadu Toughened Glass',
  'Coastal Mirror Co',
  'Vista Glass House',
  'Royal Glass Mfg',
  'NorthStar Glazing',
  'Mountain Safety Glass',
  'Pinnacle Glassworks',
  'Skyline Glazing',
  'Heritage Glass Traders',
  'Granite Glass Pvt Ltd',
  'Sundeck Glass Studio',
  'Saraswati Mirror & Glass',
  'Rajalakshmi Toughened Industries',
  'Western Ghats Glazing',
  'Hyderabad Insulated Glass',
  'Coromandel Float Glass Co'
];

// Status distribution — mix Active (~72%) / Inactive (~20%) / Blocked (~8%)
// so the demo exercises all three badge styles.
function statusForIndex(i) {
  if (i === 6 || i === 18) return 'blocked';
  if (i === 3 || i === 9 || i === 14 || i === 20 || i === 24) return 'inactive';
  return 'active';
}

// Intentional gaps so the muted-em-dash fallback is exercised in
// every group of columns (Contact + Commercial).
const NO_EMAIL_IDX  = new Set([5, 12, 21]);
const NO_MOBILE_IDX = new Set([8, 17]);
const NO_CREDIT_IDX = new Set([2, 16]);
const NO_GSTIN_IDX  = new Set([11]);

function buildSampleData() {
  const rows = [];
  MOCK_COMPANIES.forEach((name, i) => {
    const idx = i + 1;
    const state = STATES[i % STATES.length];
    const hsn = HSN_CODES[i % HSN_CODES.length];
    const status = statusForIndex(i);
    const firstWord = name.split(' ')[0].toLowerCase().replace(/[^a-z0-9]/g, '');
    rows.push({
      id: `cust-${String(idx).padStart(3, '0')}`,
      code: `CUST-${String(2000 + idx)}`,
      name,
      gstin: NO_GSTIN_IDX.has(i)
        ? null
        : `${state.code}AAAPL${String(1000 + idx).padStart(4, '0')}C1Z${idx % 10}`,
      stateCode: state.code,
      stateName: state.label,
      mobile: NO_MOBILE_IDX.has(i)
        ? null
        : `9${String(800000000 + idx * 17_341).slice(0, 9)}`,
      email: NO_EMAIL_IDX.has(i) ? null : `accounts@${firstWord}.in`,
      creditLimit: NO_CREDIT_IDX.has(i) ? null : ((i % 7) + 1) * 100_000,
      status,
      hsn
    });
  });
  return rows;
}

// Column definitions — exactly 4 groups per the Stage 6A spec:
// Customer / Contact / Tax / Commercial. Widths are explicit so
// nothing collapses and leaves a visible gap; total > viewport
// triggers a horizontal scroll instead.
function buildColumns() {
  return [
    {
      title: 'Customer Details',
      children: [
        {
          title: 'Code',
          dataIndex: 'code',
          key: 'code',
          width: 120,
          fixed: 'left',
          // Aggregate kind feeds the summary footer — `count` reads
          // the visible-page row count regardless of column type.
          aggregate: 'count',
          aggregateRender: (paged) => (
            <span className="erp-grid__summary-label">
              TOTAL · {paged.length}
            </span>
          ),
          dataType: 'string',
          sorter: (a, b) => a.code.localeCompare(b.code),
          render: (v) => <code className="erp-grid__code">{v}</code>
        },
        {
          title: 'Name',
          dataIndex: 'name',
          key: 'name',
          width: 260,
          ellipsis: true,
          dataType: 'string',
          // Inline edit (Stage 6A Increment 1) — double-click to enter
          // edit mode; Enter or blur saves; Esc reverts. Mandatory.
          editable: true,
          editorType: 'text',
          validationRules: [
            { required: true, message: 'Name is required.' },
            { min: 2, max: 120, message: '2–120 characters.' }
          ],
          sorter: (a, b) => a.name.localeCompare(b.name),
          render: (v) => <span className="erp-grid__primary">{v}</span>
        },
        {
          title: 'GSTIN',
          dataIndex: 'gstin',
          key: 'gstin',
          width: 180,
          render: (v) => <code className="erp-grid__code">{v}</code>
        }
      ]
    },
    {
      title: 'Contact Details',
      children: [
        {
          title: 'Mobile',
          dataIndex: 'mobile',
          key: 'mobile',
          width: 140,
          render: (v) => <code className="erp-grid__code">{v}</code>
        },
        {
          title: 'Email',
          dataIndex: 'email',
          key: 'email',
          width: 240,
          ellipsis: true,
          render: (v) => <span className="erp-grid__secondary">{v}</span>
        }
      ]
    },
    {
      title: 'Tax Details',
      children: [
        {
          title: 'State',
          dataIndex: 'stateName',
          key: 'stateName',
          width: 150,
          dataType: 'enum',
          // Stage 6A Increment 2 — exposes this column in the Group-by
          // toolbar dropdown. Group headers appear between rows of
          // different states; group-level credit-limit sums roll up.
          groupable: true,
          sorter: (a, b) => a.stateName.localeCompare(b.stateName),
          filters: STATES.map((s) => ({ text: s.label, value: s.label })),
          onFilter: (val, row) => row.stateName === val
        },
        {
          title: 'HSN / SAC',
          dataIndex: 'hsn',
          key: 'hsn',
          width: 120,
          filters: HSN_CODES.map((c) => ({ text: c, value: c })),
          onFilter: (val, row) => row.hsn === val,
          render: (v) => <code className="erp-grid__code">{v}</code>
        }
      ]
    },
    {
      title: 'Commercial Details',
      children: [
        {
          title: 'Credit Limit',
          dataIndex: 'creditLimit',
          key: 'creditLimit',
          width: 160,
          align: 'right',
          dataType: 'number',
          aggregate: 'sum',
          aggregateRender: (paged) => {
            const total = (paged || [])
              .map((r) => r.creditLimit)
              .filter((v) => typeof v === 'number')
              .reduce((a, b) => a + b, 0);
            return <span className="erp-grid__inr">{formatINR(total)}</span>;
          },
          sorter: (a, b) =>
            (a.creditLimit == null ? -1 : a.creditLimit) -
            (b.creditLimit == null ? -1 : b.creditLimit),
          render: (v) => <span className="erp-grid__inr">{formatINR(v)}</span>
        },
        {
          title: 'Status',
          dataIndex: 'status',
          key: 'status',
          width: 110,
          align: 'center',
          dataType: 'enum',
          // Inline-edit + Status is a great demo of editorType:select —
          // click the cell, pick from the dropdown, the row's status
          // tag updates without leaving the page.
          editable: true,
          editorType: 'select',
          editorOptions: STATUS_OPTIONS,
          groupable: true,
          filters: STATUS_OPTIONS.map((s) => ({ text: s.label, value: s.value })),
          onFilter: (val, row) => row.status === val,
          render: (v) => <ErpDataGridStatusTag value={v} />
        }
      ]
    }
  ];
}

const CustomerFormFields = memo(function CustomerFormFields({
  mode,
  existingCodes
}) {
  const isEdit = mode === 'edit';
  return (
    <>
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Customer Code"
            name="code"
            rules={[
              { required: true, message: 'Please enter a customer code.' },
              { min: 3, max: 24, message: '3–24 characters.' },
              () => ({
                validator: (_r, v) => {
                  if (!v) return Promise.resolve();
                  if (isEdit) return Promise.resolve();
                  if (existingCodes.has(String(v).trim().toUpperCase())) {
                    return Promise.reject(
                      new Error('This customer code already exists.')
                    );
                  }
                  return Promise.resolve();
                }
              })
            ]}
          >
            <Input
              prefix={<IdcardOutlined className="text-ink-400" />}
              placeholder="CUST-2050"
              disabled={isEdit}
              autoComplete="off"
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Customer Name"
            name="name"
            rules={[
              { required: true, message: 'Please enter the customer name.' },
              { min: 2, max: 120, message: '2–120 characters.' }
            ]}
          >
            <Input
              prefix={<ShopOutlined className="text-ink-400" />}
              placeholder="Sri Balaji Toughened Glass"
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item
            label="GSTIN"
            name="gstin"
            getValueFromEvent={gstinInput}
            rules={[
              {
                validator: (_r, v) =>
                  !v || isGstin(String(v).toUpperCase())
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error('Please enter a valid 15-character GSTIN.')
                      )
              }
            ]}
          >
            <Input
              placeholder="27AAAPL1234C1Z5"
              maxLength={15}
              autoCapitalize="characters"
              spellCheck={false}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label="State"
            name="stateCode"
            rules={[{ required: true, message: 'Please select a state.' }]}
          >
            <Select
              placeholder="Select state"
              options={STATES.map((s) => ({
                value: s.code,
                label: `${s.label} (${s.code})`
              }))}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Mobile (10 digits)"
            name="mobile"
            getValueFromEvent={digitsOnly(10)}
            rules={[
              {
                validator: (_r, v) =>
                  !v || isMobileIN(String(v))
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error('Please enter a valid 10-digit Indian mobile number.')
                      )
              }
            ]}
          >
            <Input
              prefix={<PhoneOutlined className="text-ink-400" />}
              placeholder="9876543210"
              maxLength={10}
              inputMode="numeric"
              autoComplete="tel"
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Email"
            name="email"
            rules={[
              {
                validator: (_r, v) =>
                  !v || isEmail(String(v))
                    ? Promise.resolve()
                    : Promise.reject(new Error('Please enter a valid email address.'))
              }
            ]}
          >
            <Input
              prefix={<MailOutlined className="text-ink-400" />}
              placeholder="accounts@example.in"
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item
            label="HSN / SAC"
            name="hsn"
            rules={[{ required: true, message: 'Please pick an HSN code.' }]}
          >
            <Select
              options={HSN_CODES.map((c) => ({ value: c, label: c }))}
              placeholder="7005"
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Credit Limit (₹)"
            name="creditLimit"
            rules={[
              {
                type: 'number',
                min: 0,
                message: 'Credit limit cannot be negative.'
              }
            ]}
          >
            <InputNumber
              min={0}
              step={10_000}
              precision={0}
              className="!w-full"
              formatter={(v) =>
                v ? `₹ ${Number(v).toLocaleString('en-IN')}` : ''
              }
              parser={(v) =>
                v ? Number(String(v).replace(/[^\d]/g, '')) : 0
              }
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Status"
            name="status"
            rules={[{ required: true, message: 'Please pick a status.' }]}
          >
            <Select
              options={STATUS_OPTIONS}
              placeholder="Active"
            />
          </Form.Item>
        </Col>
      </Row>
    </>
  );
});

// Module-scope defaults to keep prop refs stable.
const FORM_INITIAL_VALUES = (record) => ({
  status: 'active',
  creditLimit: 100_000,
  ...record
});
const FORM_TITLES = {
  add:  'Add customer',
  edit: 'Edit customer',
  view: 'Customer details'
};

// Detail-drawer definition: 4 read-only sections that mirror the
// column groups in the table. Future ERP modules can derive the same
// shape from their column config or hand-author it (this demo
// hand-authors for clarity).
function buildDetailGroups() {
  return [
    {
      title: 'Customer Details',
      items: [
        { label: 'Code', dataIndex: 'code',
          render: (v) => <code className="erp-grid__code">{v}</code> },
        { label: 'Name', dataIndex: 'name',
          render: (v) => <span className="font-medium">{v}</span> },
        { label: 'GSTIN', dataIndex: 'gstin',
          render: (v) => <code className="erp-grid__code">{v}</code> }
      ]
    },
    {
      title: 'Contact Details',
      items: [
        { label: 'Mobile', dataIndex: 'mobile',
          render: (v) => <code className="erp-grid__code">{v}</code> },
        { label: 'Email', dataIndex: 'email' }
      ]
    },
    {
      title: 'Tax Details',
      items: [
        { label: 'State', dataIndex: 'stateName' },
        { label: 'HSN / SAC', dataIndex: 'hsn',
          render: (v) => <code className="erp-grid__code">{v}</code> }
      ]
    },
    {
      title: 'Commercial Details',
      items: [
        { label: 'Credit Limit', dataIndex: 'creditLimit',
          render: (v) => <span className="erp-grid__inr">{formatINR(v)}</span> },
        { label: 'Status', dataIndex: 'status',
          render: (v) => <ErpDataGridStatusTag value={v} /> }
      ]
    }
  ];
}

export default function ErpGridDemoPage() {
  // Master controller — owns rows state, handlers, approval MFA,
  // and notification copy. Spreading `gridHandlers` onto <ErpDataGrid />
  // wires dataSource, loading, refresh, submit, delete and bulkDelete
  // in one shot.
  //
  // For this demo the operations are local-state-only PLUS approval
  // MFA — no real adapter is needed because the protected actions
  // are executed by the dedicated /api/approval-mfa/demo/* routes,
  // which are wrapped in the `action` callbacks below.
  const { rows, gridHandlers, ApprovalMfaModalSlot } = useErpMaster({
    // Stage 6A — gridId unlocks per-user, per-grid UI state
    // persistence. Hide a column, change page size, refresh → reload
    // the page and your layout is preserved. Stored under
    // `glassi:grid:{tenantId}:{userId}:demo-customer-master` in
    // localStorage. UI prefs only — never row data.
    gridId: 'demo-customer-master',
    entityLabel: { singular: 'Customer', plural: 'Customers' },
    resourceType: 'demo_customer',
    initialRows: buildSampleData,
    getId: (r) => r.id,
    getResourceLabel: (r) => `${r.code} · ${r.name}`,
    getDisplayName: (r) => r.name,
    // Stage 6A Increment 1 — a tiny in-memory adapter so the demo can
    // exercise inline edit. Real masters supply axios-backed
    // implementations. The 150 ms delay simulates a network round-trip
    // so the saving indicator is visible.
    adapter: {
      updateCell: (_id, _field, _value) =>
        new Promise((resolve) => setTimeout(resolve, 150))
    },
    approval: {
      // L1 Approval MFA — single-record delete.
      delete: {
        operationKey: 'demo.customer.delete',
        operationLabel: 'Delete customer',
        action: (challengeId, record) =>
          endpoints.approvalMfa.demoCustomerDelete(record.id, {
            approvalMfaChallengeId: challengeId
          })
      },
      // L1 + L2 Approval MFA — bulk delete. The same TOTP code can't
      // be reused between L1 and L2 (backend rejects with
      // APPROVAL_MFA_CODE_REUSED), so the user waits ~30 seconds for a
      // fresh code in the Authenticator app before completing L2.
      bulkDelete: {
        operationKey: 'demo.bulk_delete',
        // Match the resource_id format expected by the demo route's
        // getResource extractor — derived from the payload length.
        resourceId: (records) => `BULK:${records.length}`,
        action: (challengeId, records) =>
          endpoints.approvalMfa.demoBulkDelete({
            ids: records.map((r) => r.id),
            approvalMfaChallengeId: challengeId
          })
      }
    }
  });

  const existingCodes = useMemo(
    () => new Set(rows.map((r) => String(r.code).toUpperCase())),
    [rows]
  );

  const columns = useMemo(() => buildColumns(), []);
  const detailGroups = useMemo(() => buildDetailGroups(), []);

  // Stage 6A — master-detail expandable row. AntD's `expandable` prop
  // is forwarded by ErpDataGrid as-is, so any consumer can opt in
  // without grid changes. Future masters can pass an entire panel
  // here (order line items, dispatch details, customer addresses…).
  const expandable = useMemo(
    () => ({
      expandRowByClick: false,
      rowExpandable: (record) => Boolean(record),
      expandedRowRender: (record) => (
        <div className="bg-surface-50 border-l-2 border-brand-300 py-2 px-3 text-[12.5px] text-ink-700 flex flex-wrap gap-x-6 gap-y-1">
          <span>
            <span className="text-ink-500 mr-1.5">GSTIN</span>
            <code className="erp-grid__code">{record.gstin || '—'}</code>
          </span>
          <span>
            <span className="text-ink-500 mr-1.5">Mobile</span>
            <code className="erp-grid__code">{record.mobile || '—'}</code>
          </span>
          <span>
            <span className="text-ink-500 mr-1.5">Email</span>
            {record.email || '—'}
          </span>
          <span>
            <span className="text-ink-500 mr-1.5">State</span>
            {record.stateName} ({record.stateCode})
          </span>
          <span>
            <span className="text-ink-500 mr-1.5">Credit</span>
            <span className="erp-grid__inr">{formatINR(record.creditLimit)}</span>
          </span>
        </div>
      )
    }),
    []
  );

  const renderForm = useCallback(
    ({ mode }) => (
      <CustomerFormFields mode={mode} existingCodes={existingCodes} />
    ),
    [existingCodes]
  );

  // Hydrate the derived stateName column from the selected stateCode
  // before passing values up to the controller. This is entity-specific
  // (Customer carries a denormalised state name for the grid filter)
  // so it lives here, not in useErpMaster.
  const onSubmit = useCallback(
    (mode, values, original) => {
      const stateName =
        STATES.find((s) => s.code === values.stateCode)?.label || '';
      return gridHandlers.onSubmit(mode, { ...values, stateName }, original);
    },
    [gridHandlers]
  );

  return (
    <ErpMasterShell modalSlot={ApprovalMfaModalSlot}>
      {/* Stage 6A.2D-Modal — this is a DEV SANDBOX for the reusable
          ErpDataGrid component. The Add/Edit form here is the grid's
          BUILT-IN modal (renderForm + formInitialValues + formTitles)
          using the legacy CustomerFormFields component. It is intentionally
          SEPARATE from the production Customer Master surface
          (/erp/masters/customers) which uses the new centered tabbed
          modal `CustomerTabbedDrawer.jsx`. Do not confuse this page's
          old simple modal with the production UI. */}
      <Alert
        type="info" showIcon style={{ marginBottom: 8 }}
        message="Dev sandbox: ErpDataGrid demo"
        description="This page exercises the reusable grid's built-in modal form. Production Customer Master at /erp/masters/customers uses the new centered tabbed modal instead."
      />
      <ErpDataGrid
        title="Customer Master (Demo)"
        columns={columns}
        rowKey="id"
        rowHeight="compact"
        stickyHeader
        selectable
        actionWidth={96}
        pageSize={15}
        pageSizeOptions={['15', '25', '50', '100']}
        searchable
        searchPlaceholder="Search by code, name, GSTIN, mobile, email…"
        searchShortcut
        exportable
        exportFilename="customer-master"
        actions={{ add: true, view: true, edit: true, delete: true }}
        addButtonText="Add Customer"
        emptyTitle="No customers yet"
        emptyText="Add your first customer to start invoicing, dispatching and tracking credit."
        emptyCtaLabel="Add Customer"
        renderForm={renderForm}
        formTitles={FORM_TITLES}
        formInitialValues={FORM_INITIAL_VALUES}
        formModalWidth={760}
        detailGroups={detailGroups}
        detailDrawerTitle={(record) =>
          record ? record.name : 'Customer details'
        }
        detailDrawerSubtitle={(record) => (record ? record.code : null)}
        detailDrawerWidth={460}
        expandable={expandable}
        {...gridHandlers}
        onSubmit={onSubmit}
      />
    </ErpMasterShell>
  );
}
